import styled, { css } from 'styled-components'
import { Colors, Typography } from 'cio-design-library/design-tokens'
import React from 'react'

export const SelectGroup = styled.div`
  position: relative;
  display: flex;
`

const DefaultBorderPositions = {
  left: css`
    border-left: 1px solid ${Colors.Neutral60};
    border-right: 1px solid ${Colors.Neutral60};
    border-top: 1px solid ${Colors.Neutral60};
    border-bottom: 1px solid ${Colors.Neutral60};
    border-radius: 8px 0 0 8px;
  `,
  center: css`
    border-right: 1px solid ${Colors.Neutral60};
    border-top: 1px solid ${Colors.Neutral60};
    border-bottom: 1px solid ${Colors.Neutral60};
  `,
  right: css`
    border-right: 1px solid ${Colors.Neutral60};
    border-top: 1px solid ${Colors.Neutral60};
    border-bottom: 1px solid ${Colors.Neutral60};
    border-radius: 0 8px 8px 0;
  `,
}
const ActiveBorderPositions = {
  left: css`
    border-left: 1px solid white;
    border-right: 1px solid white !important;
    border-top: 1px solid white;
    border-bottom: 1px solid white;
    border-radius: 8px 0 0 8px;
  `,
  center: css`
    border-left: 1px solid white;
    border-right: 1px solid white !important;
    border-top: 1px solid white;
    border-bottom: 1px solid white;
  `,
  right: css`
    border-right: 1px solid white;
    border-left: 1px solid white;
    border-top: 1px solid white;
    border-bottom: 1px solid white;
    border-radius: 0 8px 8px 0;
  `,
}

export const InnerSelectControl = styled.div<{
  position: 'left' | 'center' | 'right'
  $height?: number
}>`
  width: 100%;
  height: ${({ $height }) => $height ?? 76}px;
  padding: 20px 12px;
  border: 1px solid transparent;
  background: rgba(255, 255, 255, 0.1);
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;

  &:not(:has(input:checked)) {
    ${({ position }) => DefaultBorderPositions[position]}
  }

  &:has(input:checked) {
    ${({ position }) => ActiveBorderPositions[position]}
  }

  & input[type='radio'] {
    display: none;
  }
`

export const SelectControlWraper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;

  &:has(input:disabled) {
    opacity: 0.5;
  }

  &:not(:has(input:disabled)) {
    label {
      cursor: pointer;
    }
  }

  label {
    color: ${Colors.Neutral50};
    svg {
      max-width: 100%;
    }
  }

  &:has(input:checked) {
    label {
      color: ${Colors.White};

      svg:not(:has(defs)) path {
        fill-opacity: 0.5;
      }

      svg stop {
        stop-opacity: 1;
      }
    }
  }
`

export const SelectControl = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof SelectControlWraper> & {
    label?: React.ReactNode
    $height?: number
    position: 'left' | 'center' | 'right'
    children: React.ReactNode
  }
>((props, ref) => {
  const { position, $height, label, children, ...rest } = props
  return (
    <SelectControlWraper ref={ref} {...rest}>
      <InnerSelectControl position={position} $height={$height}>
        {children}
      </InnerSelectControl>
      {label && label}
    </SelectControlWraper>
  )
})

export const FlexBox = styled.div<{
  flexDirection?: 'row' | 'column'
  alignItems?: string
  justifyContent?: string
  gap?: string | number
}>`
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: ${props => props.flexDirection || 'row'};
  align-items: ${props => props.alignItems || 'center'};
  justify-content: ${props => props.justifyContent || 'center'};
  gap: ${props => (typeof props.gap === 'number' ? `${props.gap}px` : props.gap || '8px')};
`

export const PlaceholderSpan = styled.span<{
  alignText?: string
  size?: keyof typeof Typography.sizes
  color?: keyof typeof Colors
}>`
  flex: 1;
  white-space: nowrap;
  color: ${props => (props.color ? Colors[props.color] : 'inherit')};
  text-align: ${props => props.alignText || 'left'};
  ${props => Typography.sizes[props.size || 'Tiny']};
`

export const Label = styled.label`
  ${Typography.sizes.Caption};
  white-space: nowrap;
`

export const ClickableOverlay = styled.span`
  position: absolute;
  inset: 0;
`
